import React, { useState } from 'react';
import { graphql } from 'gatsby';
import useStoryblok from '~/utils/storyblok-service';

import Layout from '~/components/Layout';
import Seo from '~/components/SEO';
import SidebarFilter from '~/components/SidebarFilter';
import PostPreview from '~/components/PostPreview';
import NewsletterSubscribe from '~/components/NewsletterSubscribe';
import TextCard from '~/components/Dynamic/TextCard';
import ChevronUp from '~/components/Icons/ChevronUp';

import useBlogPostPreviews from '~/hooks/useBlogPostPreviews';
import useBlogCategories from '~/hooks/useBlogCategories';
import useBlogTopics from '~/hooks/useBlogTopics';

function Resources({ data, location, pageContext }) {

  const [showMore, setShowMore] = useState(8);

  const isIndex = pageContext.field_component === 'resources_index';
  const isCategory = pageContext.field_component === 'Category';
  const isTopic = pageContext.field_component === 'topic';

  const story = useStoryblok(data.storyblokEntry, location);
  const postPreviews = useBlogPostPreviews();
  const categories = useBlogCategories();
  const topics = useBlogTopics();
  const pageName = pageContext.name;
  const filteredPostPreviews = postPreviews.filter((p) => {
    if (isIndex) {
      return true;
    }
    if (isCategory) {
      return p.content?.category?.name === pageName;
    }
    if (isTopic) {
      return p.content?.topic?.name === pageName;
    }
  });

  return (
    <Layout
      headerColor="black"
      className="text-white bg-black"
      footerColor="black"
      hideFooterCta
      hideSecondaryFooterCta
      locale={pageContext.locale}
    >
      <Seo {...story?.content?.seo} lang={story.lang} pageContext={pageContext} />

      <section className="pt-16 mb-16 text-white bg-black md:mb-24">
        <div className="container">
        {story?.content?.Hero?.length > 0 ? (
          <div className="flex flex-wrap items-center py-12 -ml-12 md:py-20 md:pb-32">
            <div className="flex flex-col pl-12 mb-16 sm:w-8/12 md:mb-0">
              <TextCard blok={story?.content?.Hero[0]?.textCard[0]} />
            </div>
          </div>

        ) : (
          <h1 className="text-h1 mt-8" dangerouslySetInnerHTML={{ __html: pageName }} />
        )}

          {isIndex && (
            <div className="flex">
              {/* blog preview */}
              <div className="flex flex-wrap -mb-12 -ml-12">
                {postPreviews.slice(0, 3).map((post, i) => (
                  <div key={`toppost${i}`} className="flex flex-col w-full pl-12 mb-12 sm:w-1/2 md:w-1/3">
                    <PostPreview post={post} imageClass={i === 1 && 'aspect-w-422 aspect-h-433'} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="my-20 md:my-24">
        <div className="container">
          <div className="py-8 border-t border-b border-[#7e7e7e] flex flex-col items-center">
            <NewsletterSubscribe className="flex-auto" type="dark" />
          </div>
        </div>
      </section>

      <section className="my-20 md:my-24">
        <div className="container">
          <div className="flex flex-wrap -ml-6">
            <aside className="flex flex-wrap w-full pl-6 md:w-3/12 justify-evenly md:block">
              <SidebarFilter pageName={pageName} className="w-full sm:w-auto" heading={pageContext?.locale?.content?.Topic} items={topics} />
              <SidebarFilter pageName={pageName} className="w-full sm:w-auto" heading={pageContext?.locale?.content?.category} items={categories} />
            </aside>

            <main className="w-full pl-6 md:w-9/12">
              <div className="flex flex-wrap -ml-8">
                {filteredPostPreviews.slice(0, showMore).map((post, i) => (
                  <div key={`resources-grid-${i}`} className="w-full pl-8 mb-12 md:w-1/2">
                    <PostPreview post={post} />
                  </div>
                ))}
              </div>

              {filteredPostPreviews.length > showMore && (
                <div className="flex justify-center">
                  <button
                    onClick={() => setShowMore(showMore + 2)}
                    className="flex items-center font-bold transition-opacity opacity-100 hover:opacity-50"
                  >
                    {pageContext?.locale?.content?.loadMore}
                    <div className="ml-2 bg-white rounded-full w-[22px] h-[22px] flex justify-center items-center transform rotate-180">
                      <div className="-mt-0.5">
                        <ChevronUp />
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </main>
          </div>
        </div>
      </section>

      <section className="text-white bg-blue">
        <div className="container py-20 text-center md:py-36">
          <h2 className="text-h2">{story?.content?.footerCTAHeading}</h2>
          <div className="flex w-full md:w-auto">
            <NewsletterSubscribe
              hideIcon
              hideTitle
              className="w-full ml-0"
              formContainerClass="pl-0"
              formClass="mx-auto mt-8 md:border-2 md:border-b-2 md:border-white !h-auto hover:text-white flex-col md:flex-row md:!inline-flex max-w-none"
              inputClass="p-6 py-5 text-center md:text-left md:border-r-0 outline-0 border-2 border-white md:border-0"
              buttonClass="py-5 pr-6 border-2 border-t-0 border-white md:border-0 justify-center md:justify-end flex items-center text-white hover:!text-white group-hover:!text-white hover:opacity-60 focus:opacity-60"
              buttonLabel={pageContext?.locale?.content?.submit}
            />
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default Resources;

export const query = graphql`
  query ResourcesIndexQuery($id: String!) {
    storyblokEntry(id: { eq: $id }) {
      id
      name
      content
      lang
      slug
      uuid
      full_slug
    }
  }
`;
